import React, { useState } from "react";
import { apiService, history } from "_helpers";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

export { InvoicePayments };

function InvoicePayments() {
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [dateRange, setDateRange] = useState("thisMonth");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("A");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [outstandingAsOnAmount, setOutstandingAsOnAmount] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { user: authUser } = useSelector((x) => x.auth);
  const [referenceNo, setReferenceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentList, setPaymentList] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [attachmentPath, setAttachmentPath] = useState(null);
  const [bankStatus, setBankStatus] = useState("Pending");
  const dispatch = useDispatch();

  const outstanding = (inv) => inv.amount - inv.paid;

  const parseDate = (dateVal) => {
    if (!dateVal) return null;

    if (dateVal instanceof Date) {
      return dateVal; // ✅ already parsed
    }

    if (typeof dateVal === "string") {
      const [dd, mm, yyyy] = dateVal.split("/");
      return new Date(`${yyyy}-${mm}-${dd}`);
    }

    throw new Error("Invalid date value");
  };

  const getOutstandingAsOnDate = () => {
    const today = new Date();

    // This Month → 1st of current month
    if (dateRange === "thisMonth") {
      return new Date(today.getFullYear(), today.getMonth(), 1);
    }

    // Previous Month → 1st of previous month
    if (dateRange === "lastMonth") {
      return new Date(today.getFullYear(), today.getMonth() - 1, 1);
    }

    // Date Wise → From Date
    if (dateRange === "custom" && fromDate) {
      return new Date(fromDate);
    }

    return new Date(today.getFullYear(), today.getMonth(), 1);
  };

  const outstandingAsOnDate = getOutstandingAsOnDate();

  const formattedOutstandingDate = outstandingAsOnDate.toLocaleDateString(
    "en-GB",
    { day: "2-digit", month: "short", year: "numeric" }
  );

  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPath(null);
  };
  const getDateRange = (dateRange, fromDate, toDate) => {
    const today = new Date();

    let startDate;
    let endDate;

    if (dateRange === "thisMonth") {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = today;
    } else if (dateRange === "lastMonth") {
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endDate = new Date(today.getFullYear(), today.getMonth(), 0); // last day prev month
    } else if (dateRange === "custom") {
      startDate = fromDate ? new Date(fromDate) : null;
      endDate = toDate ? new Date(toDate) : today;
    }

    return {
      fromDate: startDate ? parseDate(startDate) : null,
      toDate: parseDate(endDate),
    };
  };

  const openPayment = (event, param, index) => {
    event.preventDefault();

    if(param === 'OPEN'){;}
    let modal = document.querySelector("#modalPayment");
    modal.classList.toggle("closed");
  };
    
  const getInvoiceList = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_API_URL}/invoicepayment/GetInvoices`;
      const { fromDate: apiFromDate, toDate: apiToDate } = getDateRange(
        dateRange,
        fromDate,
        toDate
      );
      setToDate(apiToDate.toISOString().split("T")[0]);

      const today = normalizeDate(new Date());

      const from = normalizeDate(apiFromDate);
      const to = normalizeDate(apiToDate);

      if (from > today) {
        toast.warning("From date cannot be greater than today");
        return;
      }
      if (to > today) {
        toast.warning("To date cannot be greater than today");
        return;
      }
      if (from > to) {
        toast.warning("From date cannot be greater than To date");
        return;
      }

      const data = await apiService.get(url, dispatch, {
        customHeaders: {
          fromDate: apiFromDate.toISOString(),
          toDate: apiToDate.toISOString(),
          status: statusFilter,
          searchText: searchText,
        },
      });
      const invoices = (data?.invoices || []).map((inv) => ({
        id: inv.invoiceNumber,
        center: inv.center,
        invoiceDate: new Date(inv.invoiceDate).toLocaleDateString("en-GB"),
        dueDate: new Date(inv.dueDate).toLocaleDateString("en-GB"),
        amount: inv.invoiceAmount,
        paid: inv.invoiceAmount - inv.outstandingAmount,
        status: inv.status,
        invoice_key: inv.invoiceKey,
        center_key: inv.centerKey,
        payments: [], // load separately if needed
      }));

      setFilteredInvoices(invoices);
      const amount = data?.outstandingAsOnDate;
      setOutstandingAsOnAmount(amount);
      return invoices || [];
    } catch (error) {
      alert("Failed to fetch invoices. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const prepareSavePaymentFormData = () => {
    const formData = new FormData();

    formData.append(
      "paymentKey",
      editingPayment ? editingPayment.paymentKey : ""
    );
    formData.append("centerKey", selectedInvoice.center_key);
    formData.append("invoiceKey", selectedInvoice.invoice_key);
    formData.append("paymentDate", new Date().toISOString().substring(0, 10));
    formData.append("paymentAmount", paymentAmount);
    formData.append("paymentMode", paymentMode);
    formData.append("referenceNo", paymentMode === "Cash" ? "" : referenceNo);
    formData.append(
      "bankStatus",
      paymentMode === "Cash" ? "Deposited" : bankStatus
    );
    formData.append("notes", notes || "");

    if (attachment) {
      formData.append("attachment", attachment);
    }

    formData.append("createdBy", authUser.user_key);

    return formData;
  };

  const resetPaymentForm = () => {
    setPaymentAmount(0);
    setPaymentMode("UPI");
    setReferenceNo("");
    setNotes("");
    setAttachment(null);
    setAttachmentPath(null);
  };

  const handleSavePayment = async () => {
    try {
      if (paymentAmount <= 0) {
        toast.warning("Payment amount must be greater than zero");
        return;
      }
      setSaving(true);
      const formData = prepareSavePaymentFormData();
      const result = await saveInvoicePayment(formData);
      if (!result) {
        toast.error("Payment could not be saved");
        return;
      }
      toast.success("Payment saved successfully.");
      resetPaymentForm();
      setShowPayModal(false);
      const invoiceList = await getInvoiceList();

      const updatedInvoice = invoiceList.find(
        (inv) => inv.invoice_key === selectedInvoice.invoice_key
      );
      setSelectedInvoice(updatedInvoice);
      if (showDrawer) {
        await fetchInvoicePayments(updatedInvoice);
      }
    } catch (err) {
      toast.error(err.message || "Failed to save payment");
    } finally {
      setSaving(false);
    }
  };

  async function saveInvoicePayment(payload) {
    const result = await apiService.post(
      `${process.env.REACT_APP_API_URL}/invoicepayment/SavePayment`,
      payload,
      dispatch,
      { contentType: "multipart/form-data" }
    );

    return result;
  }

  const fetchInvoicePayments = async (invoice) => {
    setLoadingPayments(true);
    setPaymentList([]);

    const data = await apiService.get(
      `${process.env.REACT_APP_API_URL}/invoicepayment/GetPayments`,
      dispatch,
      {
        customHeaders: {
          centerKey: invoice.center_key,
          invoiceKey: invoice.invoice_key,
        },
      }
    );

    if (data) {
      setPaymentList(data);
    }
    setLoadingPayments(false);
  };

  const openViewDrawer = async (invoice) => {
    setSelectedInvoice(invoice);
    setShowDrawer(true);
    await fetchInvoicePayments(invoice);
  };
  const closeDrawer = () => {
    setShowDrawer(false);
    setSelectedInvoice(null);
    setPaymentList([]);
  };
  const openPayModal = (invoice, action, payment) => {
    setSelectedInvoice(invoice);
    setShowPayModal(true);
    if (action === "EDIT" && payment) {
      setEditingPayment(payment);
      setPaymentAmount(payment.paymentAmount);
      setPaymentMode(payment.paymentMode);
      setReferenceNo(payment.referenceNo || "");
      setNotes(payment.notes || "");
      setBankStatus(payment.bankStatus || "Pending");
      if (payment.attachmentPath) {
        setAttachmentPath(payment.attachmentPath);
      } else {
        setAttachmentPath(null);
      }
    } else {
      setEditingPayment(null);
      resetPaymentForm();
    }
  };

  const closePayModal = () => {
    setShowPayModal(false);
    resetPaymentForm();
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // size validation
    if (file.size > 5 * 1024 * 1024) {
      toast.warning("File size must be under 5MB");
      return;
    }

    const allowedTypes = ["image/", "application/pdf"];
    if (!allowedTypes.some((t) => file.type.startsWith(t))) {
      toast.warning("Only PDF or image files allowed");
      return;
    }

    setAttachment(file);
  };

  const handlePreviewAttachment = () => {
    if (attachment) {
      // Before upload (local file)
      const blobUrl = URL.createObjectURL(attachment);
      window.open(blobUrl, "_blank");
      return;
    }

    if (attachmentPath) {
      // After upload (server file)
      window.open(
        `${process.env.REACT_APP_API_URL}/${attachmentPath}`,
        "_blank"
      );
    }
  };
  const getAttachmentName = () => {
    if (attachment) return attachment.name;

    if (attachmentPath) {
      return attachmentPath.split("/").pop(); // extract filename
    }

    return "";
  };

  return (
    <div className="tele-body">
      <main>
        <div className="container-fluid px-4">
          {/* <div className="row align-items-center"> */}
          {/* LEFT: Title */}
          <div className="col-12 col-lg-4">
            <h5 className="text-blue font-roboto font-weight700 mb-1">
              Invoice Payments
            </h5>
          </div>

          <hr className="mt-0" />

          <div className="row">
              <div className="col-12 col-lg-12 col-md-12 col-sm-12 mt-2">
                  <div className="accordion" id="accordionPanelsStayOpenExample">
                      <div className="accordion-item">
                          <h2 className="accordion-header" id="panelsStayOpen-headingOne">
                              <button className="accordion-button" type="button" aria-expanded="true" >
                                  <i className="bi bi-file-earmark-medical iconcolr-w"></i>&nbsp; Manage invoice payments and partial settlements
                              </button>
                          </h2>
                          <div id="panelsStayOpen-collapseOne" className="accordion-collapse show"
                              aria-labelledby="panelsStayOpen-headingOne">
                              <div className="accordion-body">
                                  <div className="row">
                                    <div className="col-4 col-lg-2 col-md-2 col-sm-12 mt-1">
                                      <input
                                        type="text"
                                        placeholder="Search Invoice / Center"
                                        className="form-control"
                                        onChange={(e) => setSearchText(e.target.value)}
                                      />
                                    </div>
                                    <div className="col-20 col-md-2 col-lg-2 mt-1">
                                      <select
                                        className="form-select form-select-sm"
                                        id="ddlSelection"
                                        name="ddlSelection"
                                        onChange={(event) =>
                                          setDateRange(event.target.value)
                                        }
                                      >
                                        <option value="thisMonth">This Month</option>
                                        <option value="lastMonth">Previous Month</option>
                                        <option value="custom">Date Wise</option>
                                      </select>
                                    </div>
                                    {dateRange === "custom" && (
                                      <div className="col-4 col-lg-4 col-md-4 col-sm-12 mt-1">
                                        <div className="row">
                                          <div className="col-6">
                                            <input
                                              type="date"
                                              className="form-control form-control-sm"
                                              value={fromDate}
                                              onChange={(e) => setFromDate(e.target.value)}
                                            />
                                          </div>
                                          <div className="col-6">
                                            <input
                                              type="date"
                                              className="form-control form-control-sm"
                                              value={toDate}
                                              onChange={(e) => setToDate(e.target.value)}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    <div className="col-4 col-lg-2 col-md-2 col-sm-12 mt-1">
                                      <select
                                        className="form-select form-select-sm"
                                        id="ddlStatusfilter"
                                        name="ddlStatusfilter"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                      >
                                        <option value="A">All Status</option>
                                        <option value="P">Paid</option>
                                        <option value="S">Partial</option>
                                        <option value="U">Unpaid</option>
                                      </select>
                                    </div>
                                    <div className="col-4 col-lg-2 col-md-2 col-sm-12 mt-1">
                                      <button
                                        type="button"
                                        className="btn btn-dark btn-sm btn-block text-light bi bi-play-fill"
                                        id="btnApply"
                                        onClick={(event) => getInvoiceList(event)}
                                        disabled={loading}
                                      >
                                        {" "}
                                        {loading ? (
                                          <>
                                            <span
                                              className="spinner-border spinner-border-sm me-2"
                                              role="status"
                                            />
                                            Searching...
                                          </>
                                        ) : (
                                          <>Search</>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col-4 col-lg-3 col-md-4 col-sm-12 mt-1">
                                      <div className="input-group input-group-sm">
                                        <span className="input-group-text">
                                          <small>Outstanding as on</small>
                                        </span>
                                        <span className="form-control bg-light text-muted">
                                          {formattedOutstandingDate}
                                        </span>
                                        <span className="input-group-text fw-bold">
                                          ₹{outstandingAsOnAmount}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>

          <div className="row">
            <div className="col-md-12 mb-2">
              <div className="tab-content" id="tblInvoiceDtls">
                <div
                  className="tab-pane fade show active mt-2"
                  id="all-tab-pane"
                  role="tabpanel"
                  aria-labelledby="all-tab"
                  tabindex="0"
                >
                  <div className="card">
                    <div className="card-header bg-gradient-1 text-light">
                      <div className="row align-items-center text-center">
                        {/* Center – wider */}
                        <div className="col-3 col-md-2 text-start">
                          <span className="tele-table-heading">Center</span>
                        </div>

                        {/* Invoice # */}
                        <div className="col-2 col-md-1 d-none d-md-block">
                          <span className="tele-table-heading">Invoice #</span>
                        </div>

                        {/* Invoice Date – reduced */}
                        <div className="col-2 col-md-1 d-none d-md-block">
                          <span className="tele-table-heading">
                            Invoice Date
                          </span>
                        </div>

                        {/* Due Date – reduced */}
                        <div className="col-2 col-md-1 d-none d-md-block">
                          <span className="tele-table-heading">Due Date</span>
                        </div>

                        {/* Amount */}
                        <div className="col-2 col-md-2 d-none d-md-block text-end">
                          <span className="tele-table-heading">Amount</span>
                        </div>

                        {/* Outstanding */}
                        <div className="col-2 col-md-2 d-none d-md-block text-end">
                          <span className="tele-table-heading">
                            Outstanding
                          </span>
                        </div>

                        {/* Status */}
                        <div className="col-2 col-md-1 d-none d-md-block">
                          <span className="tele-table-heading">Status</span>
                        </div>

                        {/* Action */}
                        <div className="col-2 col-md-2 d-none d-md-block">
                          <span className="tele-table-heading">Action</span>
                        </div>
                      </div>
                    </div>

                    <div className="card-body tele-table-padding invoice-studylist-area">
                      {filteredInvoices.map((inv, i) => (
                        <div
                          key={inv.id}
                          className="row studylist align-items-center"
                        >
                          {/* Center */}
                          <div className="col-3 col-md-2">
                            <span>{inv.center}</span>
                          </div>

                          {/* Invoice # */}
                          <div className="col-2 col-md-1 d-none d-md-block text-center">
                            <span>{inv.id}</span>
                          </div>

                          {/* Invoice Date (reduced width) */}
                          <div className="col-2 col-md-1 d-none d-md-block text-right">
                            <span>{inv.invoiceDate}</span>
                          </div>

                          {/* Due Date (reduced width) */}
                          <div className="col-2 col-md-1 d-none d-md-block text-right">
                            <span>{inv.dueDate}</span>
                          </div>

                          {/* Amount */}
                          <div className="col-2 col-md-2 text-end d-none d-md-block">
                            <span>₹{inv.amount}</span>
                          </div>

                          {/* Outstanding */}
                          <div className="col-2 col-md-2 text-end d-none d-md-block">
                            <span>₹{outstanding(inv)}</span>
                          </div>

                          {/* Status */}
                          <div className="col-2 col-md-1 d-none d-md-block text-center">
                            <span
                              className={`status ${inv.status.toLowerCase()}`}
                            >
                              {inv.status}
                            </span>
                          </div>

                          {/* Action */}
                          <div className="col-2 col-md-2 d-none d-md-block text-center">
                            <button
                              className="btn btn-sm btn-light me-2"
                              onClick={() => {
                                openPayModal(inv, "ADD");
                              }}
                            >
                              Pay
                            </button>

                            <button
                              className="btn btn-sm btn-link p-0"
                              onClick={() => {
                                openViewDrawer(inv);
                              }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Pay Modal */}
      <div id="modalPayment" className="modal closed">

          {/*<!-- Modal content -->*/}
          <div className="payment-modal-content">
              <div className="modal-header pt-2">
                  <span className="close" id="close-button" onClick={event => openPayment(event, 'CLOSE', null)}>&times;</span>
                  <h6 className="mb-0"><i className="bi bi-person-circle"></i> Payment Against Invoice # : <span></span></h6>
                  
              </div>
              <div className="modal-body">
                  <div className="row justify-content-center pb-3">
                      <div className="col-lg-12 col-12 ps-1 pe-1">
                          <div className="form-group mt-2">
                              
                          </div>
                      </div>
                  </div>
                  <div className="row justify-content-center pb-3">
                      <div className="col-lg-12 col-12 ps-1 pe-1">
                          <div className="form-group mt-2">
                              
                          </div>
                      </div>
                  </div>

                  <div className="row justify-content-center pb-3">
                    
                    
                  </div>

                  
              </div>

              <div className="modal-footer pt-2">
                <div className="row justify-content-center pb-0">
                    <div className="col-lg-4 col-6"><button type="button"
                            className="btn btn-secondary btn-sm btn-block bi bi-save2-fill" id="btnAssignDrSave" onClick={event => openPayment(event, 'SAVE')}> SAVE </button></div>
                    <div className="col-lg-4 col-6"><button type="button"
                            className="btn btn-secondary btn-sm btn-block bi bi-x-square" id="btnAssignDrClose" onClick={event => openPayment(event, 'CLOSE')}> CANCEL </button></div>
                </div>
              </div>
          </div>
      </div>
        
        
     <div>
        {/* View Drawer */}
        {showDrawer && selectedInvoice && (
          <div className="drawer">
            <div className="drawer-header">
              <h3>Invoice #{selectedInvoice.id}</h3>
              <button onClick={() => closeDrawer()}>✕</button>
            </div>

            <div className="drawer-section">
              <strong>Summary</strong>
              <p>Amount: ₹{selectedInvoice.amount}</p>
              <p>Paid: ₹{selectedInvoice.paid}</p>
              <p>Outstanding: ₹{outstanding(selectedInvoice)}</p>
            </div>

            <div className="drawer-section">
              <strong>Payment History</strong>
              {loadingPayments ? (
                <div className="text-muted">Loading payments...</div>
              ) : paymentList.length === 0 ? (
                <div className="text-muted">No payments found</div>
              ) : (
                <table className="inner-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Mode</th>
                      <th>Ref</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentList.map((p, i) => (
                      <tr key={i}>
                        <td>
                          {new Date(p.paymentDate).toLocaleDateString("en-GB")}
                        </td>
                        <td>{p.paymentMode}</td>
                        <td>{p.referenceNo}</td>
                        <td>₹{p.paymentAmount}</td>
                        <td>{p.bankStatus}</td>
                        <td>
                          <button
                            className="btn-small"
                            onClick={() =>
                              openPayModal(selectedInvoice, "EDIT", p)
                            }
                          >
                            View/Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
